import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, Lightbulb, Sparkles, BookOpenCheck, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { ILLUSTRATION_TYPES, labelForType } from './templates';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sermon Illustrations dialog — two tabs:
 *
 *  Library: keyword + type-filtered search across the curated CC0 seed.
 *  Ask Lumera: generates a fresh illustration via Claude given topic + type
 *              and (optional) the active outline point context.
 *
 * Selecting "Insert" appends the chosen illustration to the targeted outline
 * point's content, prefixed with the title in **bold**.
 */
const SermonIllustrationsDialog = ({
  open,
  onOpenChange,
  language,
  topic,
  pointTitle,    // when launched from a specific outline point
  onInsert,      // (text) => void — appends to the active point
}) => {
  const { t } = useLanguage();
  const [tab, setTab] = useState('library');
  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const [genType, setGenType] = useState('analogy');
  const [genTopic, setGenTopic] = useState('');
  const [generated, setGenerated] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (open) {
      setQuery('');
      setType('all');
      setGenerated(null);
      setGenTopic(topic || '');
    }
  }, [open, topic]);

  // Library search runs on mount + whenever filters change while open.
  useEffect(() => {
    if (!open || tab !== 'library') return;
    let cancelled = false;
    const params = {};
    if (query.trim()) params.q = query.trim();
    if (type !== 'all') params.type = type;
    setLoadingList(true);
    axios
      .get(`${BACKEND_URL}/api/sermons/illustrations`, { params })
      .then((r) => {
        if (cancelled) return;
        setItems(r.data?.items || []);
      })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoadingList(false); });
    return () => { cancelled = true; };
  }, [open, tab, query, type]);

  const formatInsert = (title, body) => `**${title}**\n${body}`;

  const handleInsert = (title, body) => {
    onInsert?.(formatInsert(title, body));
    onOpenChange(false);
    toast.success(t('sermon.insertedIntoPoint'));
  };

  const generate = async () => {
    if (!genTopic.trim() || genTopic.trim().length < 2) {
      toast.error(t('sermon.enterTopic'));
      return;
    }
    setGenerating(true);
    setGenerated(null);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/sermons/illustrations/generate`,
        {
          topic: genTopic.trim(),
          point_title: pointTitle || '',
          type: genType,
          language,
        },
        { withCredentials: true },
      );
      setGenerated(res.data);
    } catch (err) {
      console.warn('Illustration gen failed:', err);
      toast.error(t('sermon.generationFailed'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="sermon-illustrations-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            {t('sermon.sermonIllustrations')}
          </DialogTitle>
          <DialogDescription>
            {pointTitle
              ? t('sermon.illForPoint', { title: pointTitle })
              : t('sermon.illGeneral')}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="library" data-testid="illustrations-tab-library">
              <BookOpenCheck className="w-3.5 h-3.5 mr-1" />
              {t('sermon.library')}
            </TabsTrigger>
            <TabsTrigger value="ai" data-testid="illustrations-tab-ai">
              <Sparkles className="w-3.5 h-3.5 mr-1" />
              {t('sermon.askLumera')}
            </TabsTrigger>
          </TabsList>

          {/* ───────── Library tab ───────── */}
          <TabsContent value="library" className="space-y-3 mt-3">
            <div className="flex gap-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('sermon.searchGrace')}
                data-testid="illustrations-search-input"
              />
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="w-[160px]" data-testid="illustrations-type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('sermon.allTypes')}</SelectItem>
                  {ILLUSTRATION_TYPES.map((illType) => (
                    <SelectItem key={illType} value={illType}>{labelForType(illType, language)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-1">
              {loadingList && (
                <p className="text-sm text-muted-foreground text-center py-6">
                  <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
                  {t('sermon.loading')}
                </p>
              )}
              {!loadingList && items.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">
                  {t('sermon.noResults')}
                </p>
              )}
              {items.map((it) => (
                <div
                  key={it.id}
                  className="p-3 rounded-md border border-border hover:border-primary/50 hover:bg-muted/30 transition-colors"
                  data-testid={`illustration-item-${it.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm">{it.title}</p>
                      <Badge variant="secondary" className="text-[10px] mt-1">
                        {labelForType(it.type, language)}
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleInsert(it.title, it.body)}
                      data-testid={`illustration-insert-${it.id}`}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      {t('sermon.insert')}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed whitespace-pre-line">
                    {it.body}
                  </p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ───────── AI tab ───────── */}
          <TabsContent value="ai" className="space-y-3 mt-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input
                className="sm:col-span-2"
                value={genTopic}
                onChange={(e) => setGenTopic(e.target.value)}
                placeholder={t('sermon.topicGrace')}
                data-testid="illustrations-gen-topic"
              />
              <Select value={genType} onValueChange={setGenType}>
                <SelectTrigger data-testid="illustrations-gen-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ILLUSTRATION_TYPES.map((illType) => (
                    <SelectItem key={illType} value={illType}>{labelForType(illType, language)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={generate}
              disabled={generating}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
              data-testid="illustrations-generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('sermon.composing')}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('sermon.generateIllustration')}
                </>
              )}
            </Button>

            {generated && (
              <div
                className="p-3 rounded-md border border-purple-300/50 dark:border-purple-700/50 bg-purple-50/40 dark:bg-purple-900/20"
                data-testid="illustrations-generated-preview"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="font-semibold text-sm">{generated.title}</p>
                    <Badge variant="secondary" className="text-[10px] mt-1">
                      {labelForType(generated.type, language)}
                    </Badge>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleInsert(generated.title, generated.body)}
                    data-testid="illustrations-insert-generated"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    {t('sermon.insert')}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
                  {generated.body}
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default SermonIllustrationsDialog;
